import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { getAppApiUrl } from 'shared/config/apiConfig';
import { CopyButton, Field, cx, inputCls, monoCls } from 'features/agents/WorkflowEditor/fields/inputs';

/** Origin for webhook ingress (backend), not the CRA dev server on :3000. */
export function webhookApiOrigin() {
  const explicit = getAppApiUrl().trim().replace(/\/$/, '');
  if (explicit) return explicit;
  if (process.env.NODE_ENV === 'development') return 'http://localhost:8002';
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

export function buildWebhookUrl(workflow) {
  if (!workflow?.id || !workflow?.webhook_secret) return '';
  return `${webhookApiOrigin()}/api/workflows/webhooks/${workflow.id}/${workflow.webhook_secret}`;
}

/** A read-only value with a copy button beside it. */
function CopyRow({ label, value, secret, help }) {
  const [shown, setShown] = useState(false);
  const masked = secret && !shown;
  return (
    <Field label={label} help={help}>
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          readOnly
          aria-label={label}
          value={masked ? '•'.repeat(Math.min(24, value.length || 12)) : value}
          placeholder="Save the agent to get one"
          onFocus={(e) => e.target.select()}
          className={cx(inputCls, monoCls, 'min-w-0 flex-1 truncate bg-[color:var(--surface-sunken)]')}
        />
        {secret ? (
          <button
            type="button"
            onClick={() => setShown((s) => !s)}
            aria-label={shown ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[color:var(--border-default)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
          >
            {shown ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        ) : null}
        <CopyButton text={value} label={`Copy ${label.toLowerCase()}`} disabled={!value} />
      </div>
    </Field>
  );
}

/**
 * The webhook trigger's endpoint. The URL carries the agent's secret, so it is
 * the credential: anyone who has it can start a run. When signatures are
 * required the same secret signs the body, so it gets its own row.
 */
export default function WebhookHelp({ workflow, requireHmac = false }) {
  const url = buildWebhookUrl(workflow);
  return (
    <div className="space-y-3 border-t border-[color:var(--border-subtle)] pt-3">
      <CopyRow label="Webhook URL" value={url} help="POST JSON to this URL. Treat it like a password: it includes the secret." />
      {requireHmac ? (
        <CopyRow label="Signing secret" value={workflow?.webhook_secret || ''} secret help="Sign the raw body with HMAC-SHA256 and send it as sha256=<hex>." />
      ) : null}
    </div>
  );
}
